import { getAppOrigin as getAppOriginPwaUtil } from '@salesforce/pwa-kit-react-sdk/utils/url';
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import crossFetch from 'cross-fetch';
import fetchRetry from 'fetch-retry';
import { getEnv, getMobifyProxyPrefix, isTestEnvironment, toRelativeUrl } from './url-utils';
import { isHydrated, isServer } from './utils';
import type { HttpParams } from './url-utils';

const SFCC_FETCH_RETRY_CONFIG: Parameters<typeof fetchRetry>[1] = {
    retries: 3,
    retryOn: [403, 429, 502, 503, 504, 505, 506, 507, 508, 509, 510, 511],
    retryDelay: (attempt: number) => 2 ** attempt * 600, // 600, 1200, 2400
};

const fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    isHydrated() && typeof window.fetch === 'function'
        ? window.pwaAuthFlowPromise.then(() =>
              fetchRetry(window.fetch, SFCC_FETCH_RETRY_CONFIG)(input, init),
          )
        : fetchRetry(crossFetch, SFCC_FETCH_RETRY_CONFIG)(input, init);

export const getSFCCHost: () => string = () =>
    process.env.SFCC_HOST_FOR_SERVER_SIDE ?? `https://${getEnv().host}`;

/**
 * Clean the SFCC url to add the proxy path if it's not on the server.
 * This is called to clean one url just before calling the fetch function.
 * We should not include the response of this to any react state or props.
 * @param url - The url to clean
 * @returns The cleaned url
 */
export const cleanSFCCUrl = (url: string): string => {
    if (isServer || isTestEnvironment()) {
        return url;
    }
    const prefix = getMobifyProxyPrefix();
    return `${prefix}${toRelativeUrl(url)}`;
};

export const getAppOrigin: (path?: string) => string = (path = '') =>
    isHydrated() ? `${getAppOriginPwaUtil()}${path}` : getSFCCHost();

export const SFCC_PIPELINE_BASE = '/on/demandware.store/Sites-SLT-Site/default';

function getSFCCAuthObject(): { username: string; password: string } {
    const empty = { username: '', password: '' };
    const config = getConfig();

    if (config.deployTarget === 'production' || isHydrated()) return empty;

    return { username: process.env.SFCC_USER || '', password: process.env.SFCC_PASSWORD || '' };
}

function getSFCCAuth(): string | null {
    const basicAuth = getSFCCAuthObject();
    if (basicAuth.username && basicAuth.password) {
        return `Basic ${btoa(`${basicAuth.username}:${basicAuth.password}`)}`;
    }
    return null;
}

export class SFCCHttpError extends Error {
    public readonly status: number;

    public readonly body: unknown;

    constructor(status: number, message: string, body?: unknown) {
        super(message);
        this.status = status;
        this.body = body;
    }
}

const handleHttpError = async (r: Response | Error): Promise<Response> => {
    if (r instanceof Error) {
        throw r;
    }
    if (!r.ok) {
        let body: unknown;
        if (typeof r.json === 'function') {
            try {
                body = await r.json();
            } catch (e) {
                try {
                    body = { content: await r.text() };
                } catch (e2) {
                    body = {};
                }
            }
        } else {
            body = {};
        }
        throw new SFCCHttpError(r.status, r.statusText, body);
    }
    return r;
};

export function sfccUrl(action: string, httpParams: HttpParams = []): string {
    const queryString = getQueryString(httpParams);
    return `${SFCC_PIPELINE_BASE}/${action}?${queryString}`;
}

export async function sfccGet<T>(
    action: string,
    httpParams: HttpParams = [],
    signal?: AbortSignal | undefined | null,
    isJsonResponse = true,
    returnResponse = false,
    includeCredentials = true,
): Promise<T> {
    return sfccBaseGet<T>(
        (queryString) => `${getAppOrigin()}${SFCC_PIPELINE_BASE}/${action}?${queryString}`,
        httpParams,
        signal,
        isJsonResponse,
        returnResponse,
        includeCredentials,
    );
}

export async function sfccBaseGet<T>(
    urlBuilder: (queryString: string) => string,
    httpParams: HttpParams = [],
    signal?: AbortSignal | undefined | null,
    isJsonResponse = true,
    returnResponse = false,
    includeCredentials = true,
): Promise<T> {
    const sfccAuth = getSFCCAuth();
    const baseConfig = {
        method: 'GET',
        credentials: includeCredentials ? 'same-origin' : 'omit',
        redirect: 'follow',
        cache: 'no-cache',
        headers: {
            ...(isServer
                ? {
                      'x-skip-slas-on-session': 'true',
                  }
                : {}),
        },
    } as RequestInit;
    const config = {
        ...baseConfig,
        ...(signal ? { signal } : {}),
        headers: {
            ...baseConfig.headers,
            ...(sfccAuth && isServer ? { Authorization: sfccAuth } : {}),
        },
    };
    const queryString = getQueryString(httpParams);

    return fetch(cleanSFCCUrl(urlBuilder(queryString)), config)
        .then(handleHttpError)
        .then((r) => {
            if (returnResponse) {
                return r;
            }

            return isJsonResponse ? r.json() : r.text();
        })
        .catch(handleHttpError) as Promise<T>;
}

export async function sfccPost<T>(
    action: string,
    body: object,
    queryParams: HttpParams = [],
    signal?: AbortSignal | undefined,
    method = 'POST',
    isJsonResponse = true,
): Promise<T> {
    const sfccAuth = getSFCCAuth();
    const queryString = getQueryString(queryParams);

    return fetch(
        cleanSFCCUrl(
            `${getAppOrigin()}${SFCC_PIPELINE_BASE}/${action}${
                queryString ? (action.includes('?') ? '&' : '?') + queryString : ''
            }`,
        ),
        {
            method,
            signal,
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                ...(isServer
                    ? {
                          'x-skip-slas-on-session': 'true',
                      }
                    : {}),
                ...(sfccAuth && isServer
                    ? {
                          Authorization: sfccAuth,
                      }
                    : {}),
            },
            redirect: 'follow',
            body: JSON.stringify(body),
        },
    )
        .then(handleHttpError)
        .then((r) => (isJsonResponse ? r.json() : r.text()))
        .catch(handleHttpError) as Promise<T>;
}

function getQueryString(queryParams: HttpParams): string {
    return queryParams
        .map((record) =>
            Object.entries(record)
                .map((entry) => {
                    const key = entry[0];
                    const value = entry[1];

                    return !Array.isArray(value)
                        ? `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                        : value
                              .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
                              .join('&');
                })
                .join('&'),
        )
        .join('&');
}
